import { supabase } from '../lib/supabase'
import { ALL_TIME, DateRange, inRange } from '../lib/dateRange'
import { Deal, Expense } from '../types'
import { treatmentOf, gstComponent } from '../lib/gstTreatment'

/**
 * Debtors, creditors and expenses — §6.4 and §6.5.
 *
 * All three existed as data and none of them existed as a report. Debtor
 * information was spread across the AR reminder table, the invoice ledger and a
 * dashboard tile with no aged buckets anywhere; there was no creditor side at
 * all; and "expenses" were two lump-sum columns on the deal with no categories,
 * no supplier and no invoice reference, so the expense report the spec asks for
 * had nothing to report on.
 */

/* ------------------------------------------------------------------ *
 * Debtors (§6.4)
 * ------------------------------------------------------------------ */

export type AgeBand = 'current' | 'd1_7' | 'd8_14' | 'd15_30' | 'd30_plus'

export const AGE_BAND_LABEL: Record<AgeBand, string> = {
  current: 'Current',
  d1_7: '1–7 days',
  d8_14: '8–14 days',
  d15_30: '15–30 days',
  d30_plus: '30+ days',
}

export const AGE_BANDS: AgeBand[] = ['current', 'd1_7', 'd8_14', 'd15_30', 'd30_plus']

export function bandFor(daysOverdue: number): AgeBand {
  if (daysOverdue <= 0) return 'current'
  if (daysOverdue <= 7) return 'd1_7'
  if (daysOverdue <= 14) return 'd8_14'
  if (daysOverdue <= 30) return 'd15_30'
  return 'd30_plus'
}

export interface DebtorRow {
  buyerId: string | null
  buyer: string
  creditStatus: string
  riskRating: string
  invoices: number
  outstanding: number
  oldestDays: number
  bands: Record<AgeBand, number>
}

export interface CreditorRow {
  supplier: string
  vehicles: number
  owing: number
  oldestDays: number
}

export interface DebtorsCreditorsReport {
  debtors: DebtorRow[]
  debtorTotals: { outstanding: number; invoices: number; bands: Record<AgeBand, number> }
  creditors: CreditorRow[]
  creditorTotals: { owing: number; vehicles: number }
}

const daysSince = (iso?: string | null) =>
  iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000) : 0

const emptyBands = (): Record<AgeBand, number> => ({
  current: 0,
  d1_7: 0,
  d8_14: 0,
  d15_30: 0,
  d30_plus: 0,
})

export async function loadDebtorsCreditors(
  range: DateRange = ALL_TIME
): Promise<DebtorsCreditorsReport> {
  const [{ data: allInvoices, error: invErr }, { data: buyers }, { data: allDeals, error: dealErr }] =
    await Promise.all([
      supabase
        .from('invoices')
        .select('id, buyer_id, buyer_name, total_amount, payment_amount_received, due_date, status')
        .in('status', ['sent', 'overdue', 'partial', 'disputed']),
      supabase.from('buyers').select('id, name, credit_status, risk_rating'),
      supabase
        .from('deals')
        .select('id, seller_name, valuation_amount, deal_status, created_at, paid_at')
        .is('deleted_at', null),
    ])
  if (invErr) throw invErr
  if (dealErr) throw dealErr

  // A debtor is aged on the date the invoice fell due, and a creditor on the
  // day the deal was paid — or created, where it has not been. Those are the
  // two dates the window applies to. Filtering here rather than in the query
  // keeps the ageing bands and the totals computed from the same rows.
  const invoices = ((allInvoices || []) as any[]).filter((i) => inRange(i.due_date, range))
  const deals = ((allDeals || []) as any[]).filter((d) => inRange(d.paid_at || d.created_at, range))

  const buyerById = new Map<string, any>((buyers || []).map((b: any) => [b.id, b]))

  // ---- debtors: what buyers owe us, aged on the due date.
  const byBuyer = new Map<string, DebtorRow>()
  const totals = { outstanding: 0, invoices: 0, bands: emptyBands() }

  for (const inv of (invoices || []) as any[]) {
    const outstanding =
      (Number(inv.total_amount) || 0) - (Number(inv.payment_amount_received) || 0)
    if (outstanding <= 0.005) continue

    const key = inv.buyer_id || `name:${inv.buyer_name || 'Unknown buyer'}`
    const buyer = inv.buyer_id ? buyerById.get(inv.buyer_id) : null
    const overdue = daysSince(inv.due_date)
    const band = bandFor(overdue)

    const row =
      byBuyer.get(key) ||
      ({
        buyerId: inv.buyer_id || null,
        buyer: buyer?.name || inv.buyer_name || 'Unknown buyer',
        creditStatus: buyer?.credit_status || 'ok',
        riskRating: buyer?.risk_rating || 'unrated',
        invoices: 0,
        outstanding: 0,
        oldestDays: 0,
        bands: emptyBands(),
      } as DebtorRow)

    row.invoices += 1
    row.outstanding += outstanding
    row.oldestDays = Math.max(row.oldestDays, overdue)
    row.bands[band] += outstanding
    byBuyer.set(key, row)

    totals.outstanding += outstanding
    totals.invoices += 1
    totals.bands[band] += outstanding
  }

  // ---- creditors: what we owe sellers. A deal that has been committed but not
  // yet paid is money promised and not yet gone — that is the payable.
  const OWING_STATUSES = ['committed', 'approved_for_payment']
  const bySupplier = new Map<string, CreditorRow>()
  const creditorTotals = { owing: 0, vehicles: 0 }

  for (const d of (deals || []) as Deal[]) {
    if (!OWING_STATUSES.includes(d.deal_status)) continue
    const key = d.seller_name || '—'
    const owing = Number(d.valuation_amount || 0)
    const row = bySupplier.get(key) || { supplier: key, vehicles: 0, owing: 0, oldestDays: 0 }
    row.vehicles += 1
    row.owing += owing
    row.oldestDays = Math.max(row.oldestDays, daysSince(d.created_at))
    bySupplier.set(key, row)
    creditorTotals.owing += owing
    creditorTotals.vehicles += 1
  }

  return {
    debtors: [...byBuyer.values()].sort((a, b) => b.outstanding - a.outstanding),
    debtorTotals: totals,
    creditors: [...bySupplier.values()].sort((a, b) => b.owing - a.owing),
    creditorTotals,
  }
}

/* ------------------------------------------------------------------ *
 * Expenses (§6.5)
 * ------------------------------------------------------------------ */

export const EXPENSE_CATEGORY_LABEL: Record<Expense['category'], string> = {
  transport: 'Transport / freight',
  inspection: 'Inspection',
  reconditioning: 'Reconditioning',
  ppsr: 'PPSR',
  buying_fee: 'Buying fee',
  finance_payout: 'Finance payout',
  other: 'Other',
}

export interface ExpenseRow extends Expense {
  vehicle: string
  dealStatus: string
}

export interface ExpenseReport {
  rows: ExpenseRow[]
  byCategory: { category: Expense['category']; label: string; count: number; amount: number; gst: number }[]
  totals: { amount: number; gst: number; capitalised: number; expensed: number }
}

export async function loadExpenseReport(from?: string, to?: string): Promise<ExpenseReport> {
  let q = supabase.from('expenses').select('*').order('incurred_on', { ascending: false })
  if (from) q = q.gte('incurred_on', from)
  if (to) q = q.lte('incurred_on', to)

  const { data, error } = await q
  if (error) throw error
  const expenses = (data || []) as Expense[]

  const dealIds = [...new Set(expenses.map((e) => e.deal_id).filter(Boolean))]
  const dealById = new Map<string, any>()
  if (dealIds.length) {
    const { data: deals } = await supabase
      .from('deals')
      .select('id, deal_status, vehicle_year, vehicle_make, vehicle_model, autograb_year, autograb_make, autograb_model')
      .in('id', dealIds)
    for (const d of (deals || []) as any[]) dealById.set(d.id, d)
  }

  const rows: ExpenseRow[] = expenses.map((e) => {
    const d = dealById.get(e.deal_id)
    return {
      ...e,
      vehicle: d
        ? [d.vehicle_year ?? d.autograb_year, d.vehicle_make ?? d.autograb_make, d.vehicle_model ?? d.autograb_model]
            .filter(Boolean)
            .join(' ')
        : '—',
      dealStatus: d?.deal_status || '—',
    }
  })

  const catMap = new Map<Expense['category'], { count: number; amount: number; gst: number }>()
  let amount = 0
  let gst = 0
  let capitalised = 0
  let expensed = 0

  for (const r of rows) {
    const cur = catMap.get(r.category) || { count: 0, amount: 0, gst: 0 }
    cur.count += 1
    cur.amount += Number(r.amount) || 0
    cur.gst += Number(r.gst_amount) || 0
    catMap.set(r.category, cur)

    amount += Number(r.amount) || 0
    gst += Number(r.gst_amount) || 0
    if (r.capitalised) capitalised += Number(r.amount) || 0
    else expensed += Number(r.amount) || 0
  }

  return {
    rows,
    byCategory: [...catMap.entries()]
      .map(([category, v]) => ({ category, label: EXPENSE_CATEGORY_LABEL[category], ...v }))
      .sort((a, b) => b.amount - a.amount),
    totals: { amount, gst, capitalised, expensed },
  }
}

/**
 * Record an expense against a vehicle.
 *
 * The two lump-sum columns on `deals` are kept in step, because the stocktake,
 * the commission ledger and the acquisition report all read them. That keeps
 * one number in two places, which is a real cost — but the alternative is
 * rewriting five reports at the same time as introducing the table, and the
 * totals are derived here rather than typed twice.
 */
export async function addExpense(
  input: {
    dealId: string
    category: Expense['category']
    description?: string
    amount: number
    gstAmount?: number
    supplier?: string
    invoiceRef?: string
    incurredOn?: string
    capitalised?: boolean
  },
  userId?: string
): Promise<{ ok: boolean; message: string; commissionNote?: string }> {
  const { error } = await supabase.from('expenses').insert([
    {
      deal_id: input.dealId,
      category: input.category,
      description: input.description || null,
      amount: input.amount,
      gst_amount: input.gstAmount ?? gstComponent(input.amount),
      supplier: input.supplier || null,
      invoice_ref: input.invoiceRef || null,
      incurred_on: input.incurredOn || new Date().toISOString().slice(0, 10),
      capitalised: input.capitalised ?? true,
      created_by: userId || null,
    },
  ])
  if (error) throw error

  await refreshDealCostTotals(input.dealId)

  return { ok: true, message: 'Expense recorded.' }
}

export async function deleteExpense(id: string, dealId: string): Promise<void> {
  const { error } = await supabase.from('expenses').delete().eq('id', id)
  if (error) throw error
  await refreshDealCostTotals(dealId)
}

/** Recompute deals.capitalised_transport / capitalised_repairs from the items. */
export async function refreshDealCostTotals(dealId: string): Promise<void> {
  const { data } = await supabase
    .from('expenses')
    .select('category, amount, capitalised')
    .eq('deal_id', dealId)

  let transport = 0
  let repairs = 0
  for (const e of (data || []) as any[]) {
    if (!e.capitalised) continue
    if (e.category === 'transport') transport += Number(e.amount) || 0
    else if (e.category !== 'buying_fee') repairs += Number(e.amount) || 0
  }

  await supabase
    .from('deals')
    .update({
      capitalised_transport: Math.round(transport * 100) / 100,
      capitalised_repairs: Math.round(repairs * 100) / 100,
      updated_at: new Date().toISOString(),
    })
    .eq('id', dealId)
}

/** Landed cost for one vehicle, using the §4.4 treatment. */
export function landedCostOf(deal: Deal): number {
  const buyIn = Number(deal.valuation_amount || 0)
  const cap = Number(deal.capitalised_transport || 0) + Number(deal.capitalised_repairs || 0)
  const claimable = treatmentOf(deal) === 'no_gst' ? 0 : gstComponent(buyIn)
  return Math.round((buyIn + cap - claimable) * 100) / 100
}
